import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Pencil, Trash2, Save, X,
    Calendar, Tag, Target, AlignLeft, Flag, Play, CheckCircle2, Circle, Plus, FileText,
    Timer, Clock, BarChart2,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../authentication/AuthContext';
import { useProject, useUpdateProject, useDeleteProject } from './hooks/useProjects';
import { useCreateTask, useTask } from '../task/hooks/useTasks';
import { useProjectNotes } from '../note/hooks/useNote';
import { useProjectStats } from '../reports/hooks/useProjectStats';
import AddTaskModal from '../task/components/AddTaskModal';

/* ── constants ─────────────────────────────────────────────── */
const PROJECT_CATEGORIES = [
    { value: 'WORK',      label: 'Work' },
    { value: 'PERSONAL',  label: 'Personal' },
    { value: 'EDUCATION', label: 'Education' },
    { value: 'LEARNING', label: 'Learning' },
    { value: 'HEALTH',    label: 'Health' },
    { value: 'FINANCE',   label: 'Finance' },
    { value: 'SIDE PROJECT',   label: 'Side project' },
    { value: 'OTHER',     label: 'Other' },
];

const PRIORITY_MAP = {
    1: { label: 'High',   color: 'bg-red-500/15 text-red-400 border-red-500/30' },
    2: { label: 'Medium', color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
    3: { label: 'Low',    color: 'bg-green-500/15 text-green-400 border-green-500/30' },
};

const CATEGORY_COLOR = {
    WORK:      'bg-blue-500/15 text-blue-400',
    PERSONAL:  'bg-purple-500/15 text-purple-400',
    EDUCATION: 'bg-indigo-500/15 text-indigo-400',
    HEALTH:    'bg-teal-500/15 text-teal-400',
    FINANCE:   'bg-orange-500/15 text-orange-400',
    OTHER:     'bg-gray-500/15 text-gray-400',
};

/* ── helpers ────────────────────────────────────────────────── */
function formatDate(ms) {
    if (!ms) return '—';
    return new Date(ms).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    });
}

function msToInputDate(ms) {
    if (!ms) return '';
    return new Date(ms).toISOString().split('T')[0];
}

function getProjectDate(project, field) {
    if (!project) return null;
    const camelField = field === 'start' ? 'startDate' : 'endDate';
    const snakeField = field === 'start' ? 'start_date' : 'end_date';
    return project[camelField] ?? project[snakeField] ?? null;
}

function formatNoteTime(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        + ' · '
        + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatFocusTime(seconds = 0) {
    if (!seconds) return '0m';
    if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes}m`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/* ── style helpers ──────────────────────────────────────────── */
const inputClass =
    'bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm';
const labelClass = 'text-gray-500 text-xs font-medium uppercase tracking-wide mb-1';

/* ── sub-components ─────────────────────────────────────────── */
function Section({ icon, title, children }) {
    return (
        <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-5">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">
                {icon}
                {title}
            </div>
            {children}
        </div>
    );
}

function MetaChip({ label, value }) {
    return (
        <div className="bg-[#2A2B32] rounded-xl px-4 py-3">
            <p className="text-gray-500 text-xs mb-1">{label}</p>
            <p className="text-gray-200 text-sm font-medium">{value}</p>
        </div>
    );
}

function StatBadge({ icon: Icon, label, value, colorClass }) {
    return (
        <div className="flex flex-col items-center gap-1.5 bg-[#22242C] border border-[#2A2B32] rounded-xl px-4 py-4 flex-1">
            <Icon size={16} className={colorClass} />
            <span className={`text-lg font-bold ${colorClass}`}>{value}</span>
            <span className="text-gray-500 text-xs text-center">{label}</span>
        </div>
    );
}

/* ── main component ─────────────────────────────────────────── */
export const ProjectDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authToken } = useAuth();

    const { data: project, isLoading } = useProject(authToken, id);
    const { data: tasks, isLoading: isTasksLoading } = useTask(authToken);
    const { data: projectNotes = [], isLoading: isProjectNotesLoading } = useProjectNotes(authToken, id);
    const { data: allTimeProjectStats } = useProjectStats(authToken, 'ALL_TIME');
    const { mutate: createTask } = useCreateTask(authToken);
    const { mutate: updateProject, isLoading: isSaving } = useUpdateProject(authToken, id);
    const { mutate: deleteProject } = useDeleteProject(authToken);
    const [editing, setEditing] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [form, setForm] = useState(null);

    useEffect(() => {
        if (project) {
            setForm({
                name:        project.name        || '',
                description: project.description || '',
                goal:        project.goal        || '',
                category:    project.category    || '',
                priority:    project.priority    != null ? String(project.priority) : '',
                start_date:  msToInputDate(getProjectDate(project, 'start')),
                end_date:    msToInputDate(getProjectDate(project, 'end')),
            });
        }
    }, [project]);

    const set = (field) => (e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSave = () => {
        const payload = {
            userId:      project.userId ?? project.user_id ?? undefined,
            user_id:     project.user_id ?? project.userId ?? undefined,
            name:        form.name        || undefined,
            description: form.description || undefined,
            goal:        form.goal        || undefined,
            category:    form.category    || undefined,
            priority:    form.priority    ? Number(form.priority) : undefined,
            start_date:  form.start_date  ? new Date(form.start_date).getTime() : undefined,
            end_date:    form.end_date    ? new Date(form.end_date).getTime()   : undefined,
        };
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        updateProject(payload, { onSuccess: () => setEditing(false) });
    };

    const cancelEdit = () => {
        setEditing(false);
        if (project) {
            setForm({
                name:        project.name        || '',
                description: project.description || '',
                goal:        project.goal        || '',
                category:    project.category    || '',
                priority:    project.priority    != null ? String(project.priority) : '',
                start_date:  msToInputDate(getProjectDate(project, 'start')),
                end_date:    msToInputDate(getProjectDate(project, 'end')),
            });
        }
    };

    const handleDelete = () => {
        deleteProject(id, { onSuccess: () => navigate('/projects') });
    };

    const handleCreateTask = (taskData) => {
        createTask(taskData, {
            onSuccess: () => setShowAddTaskModal(false),
        });
    };

    if (isLoading || !form) {
        return (
            <div className="min-h-screen bg-[#16171C] flex items-center justify-center">
                <div className="text-gray-400 text-sm animate-pulse">Loading…</div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-screen bg-[#16171C] flex items-center justify-center">
                <div className="text-gray-500 text-sm">Project not found.</div>
            </div>
        );
    }

    const priority      = PRIORITY_MAP[project.priority];
    const categoryColor = CATEGORY_COLOR[project.category] || CATEGORY_COLOR.OTHER;
    const projectTasks = (tasks || []).filter((task) => String(task?.projectId) === String(id));
    const completedTasks = projectTasks.filter((t) => t.taskStatus === 'COMPLETED').length;

    const projectStatsEntry = (allTimeProjectStats?.projects ?? []).find(
        (p) => String(p.projectId) === String(id)
    );
    const completedPomodoros = projectStatsEntry?.completedPomodoros ?? 0;
    const totalFocusSeconds = projectStatsEntry?.totalFocusSeconds ?? 0;

    const sortedProjectNotes = [...projectNotes].sort(
        (a, b) => new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() - new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
    );

    return (
        <div className="min-h-screen bg-[#16171C]">
            <Header />

            <div className="max-w-2xl mx-auto px-6 pt-[100px] pb-16">

                {/* Back */}
                <button
                    onClick={() => navigate('/projects')}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition mb-6"
                >
                    <ArrowLeft size={15} />
                    Back to projects
                </button>

                {/* Header card */}
                <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-6 mb-4">
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                        {project.category && (
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full ${categoryColor}`}>
                                <Tag size={10} />
                                {project.category.charAt(0) + project.category.slice(1).toLowerCase()}
                            </span>
                        )}
                        {priority && (
                            <span className={`inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full border ${priority.color}`}>
                                <Flag size={10} className="mr-1" />
                                {priority.label}
                            </span>
                        )}
                    </div>

                    {editing ? (
                        <input
                            type="text"
                            value={form.name}
                            onChange={set('name')}
                            className={`${inputClass} text-xl font-bold`}
                            placeholder="Project title"
                        />
                    ) : (
                        <h1 className="text-2xl font-bold text-gray-100 leading-snug">
                            {project.name || 'Untitled Project'}
                        </h1>
                    )}
                </div>

                {/* Sections */}
                <div className="flex flex-col gap-4 mb-4">

                    {/* Description */}
                    <Section icon={<AlignLeft size={14} />} title="Description">
                        {editing ? (
                            <textarea value={form.description} onChange={set('description')}
                                rows={3} className={`${inputClass} resize-none`}
                                placeholder="Brief overview of the project…" />
                        ) : (
                            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                                {project.description
                                    ? project.description
                                    : <span className="text-gray-600 italic">No description</span>}
                            </p>
                        )}
                    </Section>

                    {/* Goal */}
                    <Section icon={<Target size={14} />} title="Goal">
                        {editing ? (
                            <textarea value={form.goal} onChange={set('goal')}
                                rows={3} className={`${inputClass} resize-none`}
                                placeholder="What does success look like?" />
                        ) : (
                            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                                {project.goal
                                    ? project.goal
                                    : <span className="text-gray-600 italic">No goal set</span>}
                            </p>
                        )}
                    </Section>

                    {/* Category + Priority — edit only */}
                    {editing && (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col">
                                <label className={labelClass}>Category</label>
                                <select value={form.category} onChange={set('category')}
                                    className={`${inputClass} appearance-none`}>
                                    <option value="">— Select —</option>
                                    {PROJECT_CATEGORIES.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col">
                                <label className={labelClass}>Priority</label>
                                <select value={form.priority} onChange={set('priority')}
                                    className={`${inputClass} appearance-none`}>
                                    <option value="">— Select —</option>
                                    <option value="1">🔴 High</option>
                                    <option value="2">🟡 Medium</option>
                                    <option value="3">🟢 Low</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Timeline */}
                    <Section icon={<Calendar size={14} />} title="Timeline">
                        {editing ? (
                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col">
                                    <label className={labelClass}>Start Date</label>
                                    <input type="date" value={form.start_date} onChange={set('start_date')}
                                        className={`${inputClass} [color-scheme:dark]`} />
                                </div>
                                <div className="flex flex-col">
                                    <label className={labelClass}>End Date</label>
                                    <input type="date" value={form.end_date} onChange={set('end_date')}
                                        className={`${inputClass} [color-scheme:dark]`} />
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                <MetaChip label="Start" value={formatDate(getProjectDate(project, 'start'))} />
                                <MetaChip label="End"   value={formatDate(getProjectDate(project, 'end'))} />
                            </div>
                        )}
                    </Section>
                </div>

                {/* Related Tasks */}
                <Section icon={<CheckCircle2 size={14} />} title="Tasks">
                    <div className="flex justify-end mb-3">
                        <button
                            type="button"
                            onClick={() => setShowAddTaskModal(true)}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#3A3B42] bg-[#2A2B32] px-3 py-2 text-xs font-medium text-gray-200 hover:bg-[#31323A] transition"
                        >
                            <Plus size={14} />
                            Add Task
                        </button>
                    </div>

                    {isTasksLoading ? (
                        <p className="text-gray-500 text-sm animate-pulse">Loading tasks...</p>
                    ) : projectTasks.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            {projectTasks.map((task) => {
                                const isDone = task.taskStatus === 'COMPLETED';
                                return (
                                    <button
                                        key={task.id}
                                        type="button"
                                        onClick={() => navigate(`/task/${task.id}`)}
                                        className="w-full text-left flex items-center justify-between gap-3 rounded-xl border border-[#2A2B32] bg-[#22242C] px-4 py-3 hover:border-indigo-500/40 hover:bg-[#262833] transition"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            {isDone ? (
                                                <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
                                            ) : (
                                                <Circle size={14} className="text-gray-500 flex-shrink-0" />
                                            )}
                                            <span className={`text-sm truncate ${isDone ? 'line-through text-gray-500' : 'text-gray-200'}`}>
                                                {task.text}
                                            </span>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDone ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                                            {isDone ? 'Done' : 'Pending'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-sm">This project does not have any linked tasks yet.</p>
                    )}
                </Section>

                <div className="my-4">
                    <Section icon={<FileText size={14} />} title="Notes">
                        <div className="flex justify-end mb-3">
                            <button
                                type="button"
                                onClick={() => navigate(`/note/new?project=${project?.id ?? id}`)}
                                className="inline-flex items-center gap-2 rounded-lg border border-[#3A3B42] bg-[#2A2B32] px-3 py-2 text-xs font-medium text-gray-200 hover:bg-[#31323A] transition"
                            >
                                <Plus size={14} />
                                Add Note
                            </button>
                        </div>

                        {isProjectNotesLoading ? (
                            <p className="text-gray-500 text-sm animate-pulse">Loading notes...</p>
                        ) : sortedProjectNotes.length > 0 ? (
                            <div className="flex flex-col gap-2">
                                {sortedProjectNotes.map((note) => (
                                    <button
                                        key={note.id}
                                        type="button"
                                        onClick={() => navigate(`/note/${note.id}`)}
                                        className="w-full text-left rounded-xl border border-[#2A2B32] bg-[#22242C] px-4 py-3 hover:border-indigo-500/40 hover:bg-[#262833] transition"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <span className="text-sm text-gray-200 font-medium truncate">
                                                {note.title || 'Untitled Note'}
                                            </span>
                                            <span className="shrink-0 text-xs text-gray-500 tabular-nums">
                                                {formatNoteTime(note.updatedAt ?? note.createdAt)}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm">This project does not have any linked notes yet.</p>
                        )}
                    </Section>
                </div>

                {/* Stats */}
                <div className="mb-4">
                    <Section icon={<BarChart2 size={14} />} title="Stats">
                        <div className="flex gap-3">
                            <StatBadge
                                icon={Timer}
                                label="Pomodoros"
                                value={completedPomodoros}
                                colorClass="text-violet-400"
                            />
                            <StatBadge
                                icon={Clock}
                                label="Focus time"
                                value={formatFocusTime(totalFocusSeconds)}
                                colorClass="text-cyan-400"
                            />
                            <StatBadge
                                icon={CheckCircle2}
                                label="Tasks done"
                                value={completedTasks}
                                colorClass="text-green-400"
                            />
                        </div>
                    </Section>
                </div>

                {/* Action bar */}
                {editing ? (
                    <div className="flex gap-3">
                        <button
                            onClick={handleSave}
                            disabled={isSaving || !form.name.trim()}
                            className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition flex items-center justify-center gap-2"
                        >
                            <Save size={15} />
                            {isSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                        <button
                            onClick={cancelEdit}
                            className="px-5 py-3 rounded-xl border border-gray-700 text-gray-400 hover:bg-[#2A2B32] text-sm font-medium transition flex items-center gap-2"
                        >
                            <X size={15} />
                            Cancel
                        </button>
                    </div>
                ) : (
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate('/session', { state: { projectId: project?.id ?? id } })}
                            className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition flex items-center justify-center gap-2"
                        >
                            <Play size={15} />
                            Start Session
                        </button>

                        <button
                            onClick={() => setEditing(true)}
                            className="px-4 py-3 rounded-xl border border-gray-700 text-gray-300 hover:bg-[#2A2B32] text-sm font-medium transition flex items-center gap-2"
                        >
                            <Pencil size={14} />
                            Edit
                        </button>

                        {!confirmDelete ? (
                            <button
                                onClick={() => setConfirmDelete(true)}
                                className="px-5 py-3 rounded-xl border border-gray-700 text-gray-400 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/5 text-sm font-medium transition flex items-center gap-2"
                            >
                                <Trash2 size={15} />
                                Delete
                            </button>
                        ) : (
                            <div className="flex gap-2">
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition flex items-center gap-1.5"
                                >
                                    <Trash2 size={14} />
                                    Confirm
                                </button>
                                <button
                                    onClick={() => setConfirmDelete(false)}
                                    className="px-4 py-3 rounded-xl border border-gray-700 text-gray-400 hover:bg-[#2A2B32] text-sm font-medium transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {showAddTaskModal && (
                    <AddTaskModal
                        onClose={() => setShowAddTaskModal(false)}
                        onSubmit={handleCreateTask}
                        fixedProjectId={project?.id ?? id}
                        fixedProjectName={project?.name || 'Selected Project'}
                    />
                )}
            </div>
        </div>
    );
};
